// CreatorMediaPanel.tsx — images, sons et diaporamas audio d'un créateur, dans l'onglet Ressources
//
// Même présentation que les autres panneaux (barre de filtres, bascule cartes / liste, mêmes
// cartes, même liste). Image : la vignette, un clic l'affiche en grand. Son : un clic le lit
// (un son n'a pas d'image). Diaporama : la vignette, un clic lance la vidéo.
// Données : admin-api AdminResourceItems (kind = Image, Sound ou AudioSlideshow).

import React, { useEffect, useMemo, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import extra  from './VideosSection.module.scss';
import dash   from './DashboardV2.module.scss';
import { ListTable, TitleCell, WhenCell, RowActions, Dash } from './DashboardList';
import { Segment, ViewToggle, useView, plural, tintOf, size } from './dashboardShared';
import VideoPlayer from '../../widget/players/VideoPlayer';
import { API_URLS, WWW_URLS } from '../../../backendServices/urls';
import { getVerifiedMedia } from '../../../backendServices/mediaManagment/api';
import { getAdminResourceItems, AdminResourceItem } from '../../../backendServices/adminManagment/api';

export type MediaKind = 'Image' | 'Sound' | 'AudioSlideshow';

const WORDS: Record<MediaKind, { one: string; many: string; the: string; play: string; masc: boolean; empty: string }> = {
  Image         : { one: 'image'   , many: 'images'    , the: 'images'             , play: 'Voir'    , masc: false, empty: 'Aucune image.'            },
  Sound         : { one: 'son'     , many: 'sons'      , the: 'sons'               , play: 'Écouter' , masc: true , empty: 'Aucun son.'               },
  AudioSlideshow: { one: 'diaporama', many: 'diaporamas', the: 'diaporamas audio'  , play: 'Lire'    , masc: true , empty: 'Aucun diaporama audio.'   },
};

type Sort = 'recent' | 'name' | 'size';
const byDateDesc = (a: AdminResourceItem, b: AdminResourceItem) => (b.date ?? '').localeCompare(a.date ?? '');
const COMPARE: Record<Sort, (a: AdminResourceItem, b: AdminResourceItem) => number> = {
  recent: byDateDesc,
  name  : (a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base', numeric: true }),
  size  : (a, b) => (b.bytes ?? 0) - (a.bytes ?? 0) || byDateDesc(a, b),
};

const thumbOf = (m: AdminResourceItem) => m.publicRef && m.image ? `${WWW_URLS.MediaServer}${m.publicRef}/images/thumbnail.webp` : '';

/** Vignette : l'image ; un son n'en a pas (note de musique) ; à défaut, le nom sur une couleur. */
const Pic: React.FC<{ m: AdminResourceItem; kind: MediaKind; mini?: boolean; onOpen?: (m: AdminResourceItem) => void }> = ({ m, kind, mini, onOpen }) => {
  const [ failed, setFailed ] = useState(false);
  const src = thumbOf(m);
  const img = src && !failed ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} /> : null;
  const note = kind === 'Sound' ? <span style={{ fontSize: mini ? 18 : 44, opacity: 0.8 }} aria-hidden="true">♪</span> : null;
  if (mini) return <div style={{ width: '100%', height: '100%', background: tintOf(m.id), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{img ?? note}</div>;
  const open = onOpen ? { role: 'button', tabIndex: 0, 'aria-label': `${WORDS[kind].play} ${m.title}`, style: { cursor: 'pointer', background: tintOf(m.id) },
                          onClick: () => onOpen(m),
                          onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(m); } } }
                        : { style: { background: tintOf(m.id) } };
  return (
    <div className={styles.thumb} {...open}>
      {img ?? note ?? <span className={styles.thumbName}>{m.title}</span>}
      <span className={styles.badges} style={{ zIndex: 1, position: 'absolute' }}>
        <span className={`${styles.badge} ${m.active ? styles.badgeOn : styles.badgeOff}`}>{m.active ? 'ACTIF' : 'INACTIF'}</span>
      </span>
    </div>
  );
};

const StateBadge: React.FC<{ m: AdminResourceItem }> = ({ m }) =>
  <span className={`${styles.badge} ${m.active ? styles.badgeOn : styles.badgeOff}`}>{m.active ? 'ACTIF' : 'INACTIF'}</span>;

const CreatorMediaPanel: React.FC<{ adminId: number; kind: MediaKind }> = ({ adminId, kind }) => {

  const w = WORDS[kind];
  const [ items   , setItems    ] = useState<AdminResourceItem[]>([]);
  const [ loading , setLoading  ] = useState(true);
  const [ error   , setError    ] = useState(false);
  const [ state   , setState    ] = useState<'all' | 'on' | 'off'>('all');
  const [ query   , setQuery    ] = useState('');
  const [ sort    , setSort     ] = useState<Sort>('recent');
  const [ view    , setView     ] = useView(`adminResources.${kind}View`);
  const [ opened  , setOpened   ] = useState<{ m: AdminResourceItem; url?: string; error?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    getAdminResourceItems(adminId, kind).then(r => {
      if (cancelled) return;
      if (r.code === 1) setItems(r.payload.items); else setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [adminId, kind]);

  const open = async (m: AdminResourceItem) => {
    if (kind === 'AudioSlideshow') {
      setOpened({ m });
      const url = m.publicRef ? await getVerifiedMedia([`${WWW_URLS.MediaServer}${m.publicRef}/videos/001/index.m3u8`]) : '';
      setOpened({ m, url: url || undefined, error: url ? undefined : 'Vidéo indisponible (encore en transcodage ?).' });
    } else setOpened({ m });
  };

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(m => !q || m.title.toLowerCase().includes(q));
  }, [items, query]);

  const shown = useMemo(() => matching.filter(m => state === 'all' || (state === 'on') === !!m.active).sort(COMPARE[sort]), [matching, state, sort]);

  const on = matching.filter(m => m.active).length;

  const Play: React.FC<{ m: AdminResourceItem }> = ({ m }) => (
    <div className={styles.actions}>
      <button type="button" className={styles.primary} disabled={!m.publicRef && kind !== 'Sound'} onClick={() => open(m)}>{w.play}</button>
    </div>
  );

  if (loading) return <span>Chargement des {w.the}…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (items.length === 0) return <div className={styles.empty}>{w.empty}</div>;

  const filtersOn = query.trim() !== '' || state !== 'all';

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Titre…" aria-label={`Rechercher ${w.masc ? 'un' : 'une'} ${w.one}`}
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', `Tous ${matching.length}`], ['on', `Actifs ${on}`], ['off', `Inactifs ${matching.length - on}`] ] as const} />
        {filtersOn && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, w.one, w.many)}</strong> sur {items.length}</span>
          <button type="button" className={extra.reset} onClick={() => { setQuery(''); setState('all'); }}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            <option value="recent">{w.masc ? 'Plus récents' : 'Plus récentes'}</option>
            <option value="name">Nom (A → Z)</option>
            <option value="size">{w.masc ? 'Plus lourds' : 'Plus lourdes'}</option>
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {shown.length === 0 && <div className={styles.empty}>Aucun résultat.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(m => (
            <article key={m.id} className={styles.card}>
              <Pic m={m} kind={kind} onOpen={open} />
              <div className={styles.cardBody}>
                <span className={styles.cardName} title={m.title}>{m.title || '—'}</span>
                <div className={styles.chips}>
                  {size(m.bytes ?? 0) && <span>{size(m.bytes ?? 0)}</span>}
                  {m.date && <span>Ajouté le {new Date(m.date).toLocaleDateString('fr-FR')}</span>}
                </div>
                <Play m={m} />
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={m => m.id} dateOf={m => m.date}
                   noun={{ one: w.one, many: w.many }}
                   sort={sort === 'recent' ? null : sort} onSort={id => setSort((id ?? 'recent') as Sort)}
                   columns={[
          { id: 'name', label: w.one.charAt(0).toUpperCase() + w.one.slice(1), sort: COMPARE.name, cell: m =>
              <TitleCell thumb={<Pic m={m} kind={kind} mini />} title={m.title || '—'} titleHint={m.title} /> },
          { id: 'state', label: 'État', cell: m => <StateBadge m={m} /> },
          { id: 'size', label: 'Taille', right: true, sort: COMPARE.size, cell: m => size(m.bytes ?? 0) || <Dash/> },
          { id: 'date', label: 'Ajouté', cell: m => <WhenCell iso={m.date} /> },
          { id: 'actions', label: '', right: true, cell: m => <RowActions><Play m={m} /></RowActions> },
        ]} />
      )}

      {opened && (
        <div className={dash.overlay} onClick={() => setOpened(null)}>
          <div className={dash.modal} onClick={e => e.stopPropagation()}>
            <div className={dash.sectionHeader}>
              <span>{opened.m.title}</span>
              <button className={dash.refresh} onClick={() => setOpened(null)} title="Fermer">✕</button>
            </div>
            {kind === 'Image' && opened.m.publicRef &&
              // Le rendu pleine résolution n'existe que pour les images récentes : repli sur les vignettes.
              <img className={dash.zoomMedia} alt={opened.m.title} src={`${WWW_URLS.MediaServer}${opened.m.publicRef}/images/image.webp`}
                   onError={e => { const img = e.currentTarget;
                                   if      (img.src.endsWith('/image.webp'))         img.src = `${WWW_URLS.MediaServer}${opened.m.publicRef}/images/thumbnail_BIG.webp`;
                                   else if (img.src.endsWith('/thumbnail_BIG.webp')) img.src = `${WWW_URLS.MediaServer}${opened.m.publicRef}/images/thumbnail.webp`; }} />}
            {kind === 'Sound' &&
              // Le rendu AAC public n'existe que pour les sons transcodés récents : repli sur la source, par l'API.
              <audio className={dash.zoomAudio} controls autoPlay
                     src={opened.m.publicRef ? `${WWW_URLS.MediaServer}${opened.m.publicRef}/sounds/audio.aac` : API_URLS.getSoundFile.replace('{id}', `${opened.m.id}`)}
                     onError={e => { const a = e.currentTarget; const repli = API_URLS.getSoundFile.replace('{id}', `${opened.m.id}`); if (a.src !== repli) a.src = repli; }} />}
            {kind === 'AudioSlideshow' && (opened.url ? <VideoPlayer url={opened.url} /> : <div className={styles.empty}>{opened.error ?? 'Chargement…'}</div>)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorMediaPanel;
